import React from 'react';
import {
  CAT_SWATCH, DASH, fmt1, fmtDay, fmtInt, openCountText, pairTone, shortOpenStatus, toneOf,
  whenLines
} from './briefingFormat';

/** Number with green (better) or red (worse) colour; plain when there is no change. */
export const Tone = ({ tone, children }) => (tone ? <span className={tone}>{children}</span> : <>{children}</>);

const isNum = (value) => value != null && value !== '' && Number.isFinite(Number(value));

/** "before → after" with the after value coloured by whether it improved. */
export const Arrow = ({ prev, next, format = fmt1, suffix = '', betterWhen = 'lower' }) => {
  const show = (value) => (isNum(value) ? `${format(value)}${suffix}` : DASH);
  return (
    <>
      {show(prev)} &rarr; <Tone tone={pairTone(prev, next, betterWhen)}>{show(next)}</Tone>
    </>
  );
};

export const Kpi = ({ value, label, note }) => (
  <div className="kpi">
    <b>{value}</b>
    <span>{label}</span>
    <em>{note}</em>
  </div>
);

/** A change cell such as "+1.2", coloured by whether it is better for the service. */
export const ChangeText = ({ text, value, betterWhen = 'lower' }) => (
  <Tone tone={toneOf(value, betterWhen)}>{text}</Tone>
);

const EmptyRow = ({ cols, text }) => (
  <tr>
    <td colSpan={cols}>{text}</td>
  </tr>
);

/** Open cases: topic, opened, status. */
const OpenNowTable = ({ rows, id }) => (
  <table className="raise" id={id}>
    <thead>
      <tr><th>Topic</th><th>Opened</th><th>Status</th></tr>
    </thead>
    <tbody>
      {rows.length === 0 ? <EmptyRow cols={3} text="No open cases." /> : rows.map((row, index) => (
        <tr className="open" key={`${row.caseNumber || row.topic}-${index}`}>
          <td>{row.topic || row.subject}</td>
          <td>{fmtDay(row.opened)}</td>
          <td>{shortOpenStatus(row)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const OpenNowBlock = ({ rows, id }) => (
  <div>
    <h2>Open now</h2>
    <p className="note">{openCountText(rows.length)}</p>
    <OpenNowTable rows={rows} id={id} />
  </div>
);

const ClosedSinceAzureBlock = ({ rows, id }) => (
  <div>
    <h2>Closed since the Azure move</h2>
    <p className="note">
      {rows.length === 0
        ? 'No closed Connect availability or preview issues opened on or after 27 June 2026.'
        : 'Closed Connect availability and preview issues opened on or after 27 June 2026.'}
    </p>
    <table className="raise" id={id}>
      <thead>
        <tr><th>Topic</th><th>When</th><th>Outcome</th></tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <EmptyRow cols={3} text="No matching closed cases since the Azure move." />
        ) : rows.map((row, index) => (
          <tr key={`${row.caseNumber || row.topic}-${index}`}>
            <td>{row.topic || row.subject || DASH}</td>
            <td>{whenLines(row).map((line, i) => <React.Fragment key={line}>{i > 0 && <br />}{line}</React.Fragment>)}</td>
            <td>{row.outcome || row.ending || DASH}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Two-column discuss grid used on both summary tabs. */
export const OpenAndClosed = ({ briefing, prefix }) => (
  <div className="discuss">
    <OpenNowBlock rows={briefing.openNow || []} id={`${prefix}-open-now`} />
    <ClosedSinceAzureBlock rows={briefing.closedSinceAzure || []} id={`${prefix}-closed-azure`} />
  </div>
);

/** Closed-case counts by how long they stayed open. */
export const PaceColumn = ({ pace, title }) => {
  if (!pace) {
    return (
      <div>
        <h3>{title}</h3>
        <p className="note">No closed cases.</p>
      </div>
    );
  }
  const buckets = pace.buckets || [];
  const max = Math.max(1, ...buckets.map((bucket) => bucket.count));
  return (
    <div>
      <h3>{title} &middot; {fmtInt(pace.closedCount)} closed</h3>
      {buckets.map((bucket) => (
        <div className="bar-row" key={bucket.label}>
          <div>{bucket.label}</div>
          <div className="track">
            <div className={`fill${bucket.dark ? ' warn' : ''}`} style={{ width: `${Math.round((bucket.count / max) * 1000) / 10}%` }} />
          </div>
          <div>{fmtInt(bucket.count)} &middot; {fmtInt(bucket.pct)}%</div>
        </div>
      ))}
    </div>
  );
};

export const CategoryName = ({ name }) => (
  <>
    <i className="swatch" style={{ background: CAT_SWATCH[name] || '#999' }} />
    {name}
  </>
);

/** Availability cases (service reported down). */
export const AvailabilityTable = ({ rows }) => (
  <table className="status">
    <thead>
      <tr>
        <th className="fit">Opened</th>
        <th className="fit">Case</th>
        <th className="fit">Severity</th>
        <th className="fit">Case age</th>
        <th>What was reported</th>
        <th>Outcome</th>
      </tr>
    </thead>
    <tbody>
      {(rows || []).length === 0 ? (
        <EmptyRow cols={6} text="No availability cases in lake." />
      ) : rows.map((row, index) => (
        <tr key={`${row.caseNumber}-${index}`}>
          <td className="fit">{fmtDay(row.opened)}</td>
          <td className="fit">{row.caseNumber || ''}</td>
          <td className="fit">{row.sev || DASH}</td>
          <td className="fit">{row.ageLabel || DASH}</td>
          <td>{row.reported || ''}</td>
          <td>{row.outcome || DASH}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** One row per theme or category with counts and rates for the two periods. */
export const RateTable = ({ rows, labelHead, firstLabel, secondLabel, renderName = (r) => r.theme, total, emptyText }) => (
  <table className="nums">
    <thead>
      <tr>
        <th>{labelHead}</th><th>{firstLabel}</th><th>Per 30 days</th><th>{secondLabel}</th><th>Per 30 days</th><th>Change</th>
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 && !total ? <EmptyRow cols={6} text={emptyText} /> : null}
      {rows.map((row) => (
        <tr key={row.category || row.theme}>
          <td className={row.theme ? 'fit' : undefined}>{renderName(row)}</td>
          <td>{fmtInt(row.y25)}</td>
          <td>{fmt1(row.r25)}</td>
          <td>{fmtInt(row.y26)}</td>
          <td>{fmt1(row.r26)}</td>
          <td><ChangeText text={`${row.chg > 0 ? '+' : ''}${fmt1(row.chg)}`} value={row.chg} /></td>
        </tr>
      ))}
      {total}
    </tbody>
  </table>
);

